import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  RiCloseLine,
  RiUploadCloud2Line,
  RiDeleteBin6Line,
  RiCheckboxCircleFill,
  RiCheckLine,
} from '@remixicon/react';
import { toast } from 'sonner';
import { isMfaCancelled } from '../hooks/MfaStepUpGate';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// S3 keys are stored with a UUID prefix (orgId for the standard contract,
// quoteId for per-quote overrides) baked into the filename to avoid
// collisions — e.g. `contracts/94d7...cf5fb9_MyContract.pdf`. That
// prefix is bookkeeping, not something a user typed, so strip it before
// display. Leaves untagged filenames untouched.
const UUID_PREFIX_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;
const filenameFromUrl = (url = '') => {
  try {
    const path = url.split('?')[0];
    const last = path.split('/').pop();
    return decodeURIComponent(last || '').replace(UUID_PREFIX_RE, '');
  } catch {
    return '';
  }
};

const PdfFileIcon = () => (
  <div className="relative w-10 h-12 flex-shrink-0">
    <svg viewBox="0 0 40 48" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 4a4 4 0 0 1 4-4h20l12 12v32a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V4Z"
        fill="#FFFFFF"
        stroke="#E5E7EB"
        strokeWidth="1"
      />
      <path d="M28 0l12 12h-8a4 4 0 0 1-4-4V0Z" fill="#F3F4F6" />
    </svg>
    <span className="absolute bottom-1 left-1 right-1 text-center text-[8px] font-bold tracking-wider text-white bg-red-500 rounded-sm py-[1px]">
      PDF
    </span>
  </div>
);

const StandardContractModal = ({ open, onClose, initialUrl = '', initialFile = null, onSubmit }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [existingUrl, setExistingUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(initialFile || null);
      setExistingUrl(initialUrl || '');
      setSaving(false);
    }
  }, [open, initialFile, initialUrl]);

  if (!open) return null;

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile({ name: f.name, size: f.size, type: f.type, raw: f });
    setExistingUrl('');
  };

  const handleRemove = () => {
    setFile(null);
    setExistingUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      await onSubmit?.({ file, existingUrl });
      onClose();
      toast.success('Contract updated successfully', { position: 'bottom-right' });
    } catch (err) {
      if (isMfaCancelled(err)) return; // user closed the step-up modal — silent
      toast.error(err?.message || 'Failed to update contract', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  const displayName = file?.name || filenameFromUrl(existingUrl) || '';
  const displaySize = file?.size ? formatSize(file.size) : '';
  const hasFile = !!file || !!existingUrl;
  const canSubmit = hasFile && !saving;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h3 className="text-lg font-semibold text-primary-700">Standard Charter Contract</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2.5">
            Upload Standard Charter Contract PDF<span className="text-red-500 ml-0.5">*</span>
          </label>

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="hidden"
          />

          {hasFile ? (
            <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl">
              <PdfFileIcon />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                  {displaySize && (
                    <>
                      {displaySize}
                      <span className="text-gray-300">·</span>
                    </>
                  )}
                  <RiCheckboxCircleFill className="w-3.5 h-3.5 text-green-500" />
                  <span>Completed</span>
                </p>
              </div>
              <button
                type="button"
                onClick={handleRemove}
                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                aria-label="Remove contract"
              >
                <RiDeleteBin6Line className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-4 py-10 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <RiUploadCloud2Line className="w-7 h-7 text-gray-400" />
              <p className="text-sm text-black">Choose a file</p>
              <p className="text-xs text-gray-500">PDF, up to 5 MB.</p>
              <span className="mt-1 text-sm text-gray-900 underline-offset-2 rounded-full py-1 px-2 hover:underline border border-gray-200">
                Browse File
              </span>
            </button>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={!canSubmit}
            className={`mt-6 w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
              canSubmit
                ? 'bg-primary-700 text-white hover:bg-primary-800'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <RiCheckLine className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default StandardContractModal;
